export type PhoneVerificationStep = 'send' | 'verify';

type ErrorLike = {
  status?: number | string;
  data?: {
    message?: unknown;
    error?: unknown;
    retryAfter?: unknown;
    retry_after?: unknown;
  };
};

const GENERIC_FALLBACK = 'Something went wrong. Please try again.';

const formatRetryAfter = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds <= 0) return 'a moment';
  if (seconds < 60) {
    const s = Math.round(seconds);
    return `${s} ${s === 1 ? 'second' : 'seconds'}`;
  }
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
};

const extractRetryAfter = (data: ErrorLike['data']): string | null => {
  const raw = data?.retryAfter ?? data?.retry_after;
  if (raw == null) return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return formatRetryAfter(n);
};

const rateLimitMessage = (data: ErrorLike['data']): string => {
  const retryAfter = extractRetryAfter(data);
  return retryAfter
    ? `Too many attempts. Please try again in ${retryAfter}.`
    : 'Too many attempts. Please try again later.';
};

const stepFallback = (step: PhoneVerificationStep, status: number | undefined): string => {
  if (step === 'send') {
    if (status === 400) return 'Please enter a valid phone number including country code.';
    if (status === 409) return 'This phone number is already registered to another account.';
    return GENERIC_FALLBACK;
  }
  // verify
  if (status === 400) return 'Invalid verification code. Please check and try again.';
  if (status === 404 || status === 410) return 'Verification code has expired. Please request a new one.';
  return GENERIC_FALLBACK;
};

const asString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const getPhoneVerificationErrorMessage = (
  error: unknown,
  step: PhoneVerificationStep,
): string => {
  if (!error || typeof error !== 'object') return GENERIC_FALLBACK;

  const err = error as ErrorLike;
  const status = typeof err.status === 'number' ? err.status : undefined;
  const backendMessage = asString(err.data?.message) ?? asString(err.data?.error);

  if (status === 429) {
    return rateLimitMessage(err.data);
  }

  if (backendMessage) {
    return backendMessage;
  }

  return stepFallback(step, status);
};
